import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentsOnlineApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, ArrowsClockwise } from '@phosphor-icons/react';
import { useLanguage } from '../contexts/LanguageContext';

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 20;

export default function PaymentSuccess() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: 'verifying', data: null, error: '' });

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const reference = (() => {
      try { return sessionStorage.getItem('geniuspay_last_ref') || ''; } catch { return ''; }
    })();

    if (!reference) {
      setState({ phase: 'error', error: language === 'fr' ? 'Référence introuvable' : 'Reference not found' });
      return;
    }

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await paymentsOnlineApi.geniuspayStatus(reference);
        const status = (data?.status || '').toLowerCase();
        if (status === 'completed' || data?.processed) {
          if (!cancelled) {
            setState({ phase: 'success', data, error: '' });
            try { sessionStorage.removeItem('geniuspay_last_ref'); } catch {}
          }
          return;
        }
        if (['failed', 'cancelled', 'expired'].includes(status)) {
          if (!cancelled) setState({ phase: 'failed', data, error: status });
          return;
        }
        if (attempts < MAX_POLLS) setTimeout(tick, POLL_INTERVAL_MS);
        else if (!cancelled) setState({ phase: 'pending', data, error: '' });
      } catch (e) {
        if (!cancelled) setState({ phase: 'error', error: e.response?.data?.detail || 'Error' });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [language]);

  const { phase, data, error } = state;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-emerald-200" data-testid="payment-success-page">
        <CardContent className="p-8 text-center space-y-4">
          {phase === 'verifying' && (
            <>
              <ArrowsClockwise size={48} className="mx-auto text-emerald-600 animate-spin" />
              <h1 className="font-outfit text-xl font-bold">{language === 'fr' ? 'Vérification du paiement…' : 'Verifying payment...'}</h1>
              <p className="text-sm text-slate-600">{language === 'fr' ? 'Nous confirmons la transaction auprès de GeniusPay.' : 'Confirming with GeniusPay.'}</p>
            </>
          )}
          {phase === 'success' && (
            <>
              <CheckCircle size={56} className="mx-auto text-emerald-600" weight="fill" />
              <h1 className="font-outfit text-2xl font-bold text-emerald-700">{language === 'fr' ? 'Paiement confirmé !' : 'Payment confirmed!'}</h1>
              <p className="text-sm text-slate-700">
                {language === 'fr' ? 'Montant :' : 'Amount:'}{' '}
                <strong>{Number(data?.amount_xof || 0).toLocaleString('fr-FR')} FCFA</strong>
                {data?.label ? <> — {data.label}</> : null}
              </p>
              <Button onClick={() => navigate('/parent')} className="bg-emerald-600 hover:bg-emerald-700 mt-4" data-testid="back-to-dashboard-btn">
                {language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
              </Button>
            </>
          )}
          {phase === 'pending' && (
            <>
              <ArrowsClockwise size={48} className="mx-auto text-amber-500" />
              <h1 className="font-outfit text-xl font-bold">{language === 'fr' ? 'Paiement en cours…' : 'Payment in progress...'}</h1>
              <p className="text-sm text-slate-600">{language === 'fr' ? 'La confirmation peut prendre quelques minutes. Vous serez notifié.' : 'Confirmation may take a few minutes.'}</p>
              <Button onClick={() => navigate('/parent')} variant="outline">
                {language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
              </Button>
            </>
          )}
          {(phase === 'failed' || phase === 'error') && (
            <>
              <XCircle size={56} className="mx-auto text-red-500" weight="fill" />
              <h1 className="font-outfit text-xl font-bold text-red-600">
                {language === 'fr' ? 'Paiement non confirmé' : 'Payment not confirmed'}
              </h1>
              <p className="text-sm text-slate-700">{error || (language === 'fr' ? 'La transaction n\'a pas abouti.' : 'Transaction did not complete.')}</p>
              <Button onClick={() => navigate('/parent')} variant="outline">
                {language === 'fr' ? 'Retour' : 'Back'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
